import { useEffect } from 'react'
import { usePreference } from './preferences'

// The app-wide text size (h 1533-1574). Three steps set the root font size;
// every length in the stylesheets is in rem, so type, controls, the rail
// (234 → 255 → 281px) and any fixed-height box grow together and the layout
// keeps its structure. Never CSS zoom or a transform (h 1548-1552).
//
// It is the person's, not the browser's (h 1570-1572): stored as the
// preference ui.text_size, so it follows them to any machine. The last
// choice made in this browser is also kept locally with whose it was, and
// applied at boot (main.tsx) before the first render, so the page does not
// draw at 100% and then reflow once the preference arrives.

export type TextSize = 'default' | 'large' | 'xlarge'

export const TEXT_SIZES: { key: TextSize; pct: number; label: string }[] = [
  { key: 'default', pct: 100, label: 'Default' },
  { key: 'large', pct: 109, label: 'Large' },
  { key: 'xlarge', pct: 120, label: 'Extra large' },
]

export const TEXT_SIZE_PREF = 'ui.text_size'
const LOCAL_KEY = 'dp.textSize'

const isSize = (v: unknown): v is TextSize => TEXT_SIZES.some((s) => s.key === v)

export function applyTextSize(size: TextSize) {
  const step = TEXT_SIZES.find((s) => s.key === size) || TEXT_SIZES[0]
  const html = document.documentElement
  html.style.fontSize = step.pct === 100 ? '' : `${step.pct}%`
  html.dataset.textSize = step.key
}

// The last choice made in this browser, and whose it was.
function readLocal(): { uid: string; size: TextSize } | null {
  try {
    const v = JSON.parse(localStorage.getItem(LOCAL_KEY) || 'null')
    return v && typeof v.uid === 'string' && isSize(v.size) ? v : null
  } catch {
    return null
  }
}

function writeLocal(uid: string, size: TextSize) {
  try {
    if (size === 'default') localStorage.removeItem(LOCAL_KEY)
    else localStorage.setItem(LOCAL_KEY, JSON.stringify({ uid, size }))
  } catch {
    /* private mode: the server copy still applies after sign-in */
  }
}

// At boot, before anyone is known: the last choice made here. Most machines
// have one person on them; on a shared one the next person's own setting (or
// the default) replaces it as soon as they are signed in.
export function applyStoredTextSize() {
  const v = readLocal()
  applyTextSize(v ? v.size : 'default')
}

interface TokenSource {
  getIdToken: () => Promise<string>
  uid?: string
}

// [size, set]. Mounted once, by the shell, which applies it. Preferences are
// kept per workspace (routes/preferences.js); a workspace where this person
// has never chosen keeps the choice they made here, so switching workspace
// does not change their text size.
export function useTextSize(user: TokenSource | null | undefined): [TextSize, (s: TextSize) => void] {
  const local = readLocal()
  const mine = local && user?.uid && local.uid === user.uid ? local.size : 'default'
  const [stored, set, loaded] = usePreference<TextSize>(user, TEXT_SIZE_PREF, mine)
  const size = isSize(stored) ? stored : 'default'
  useEffect(() => {
    // Until the read lands, the boot value stands (no flash back to 100%).
    if (!loaded) return
    applyTextSize(size)
    if (user?.uid) writeLocal(user.uid, size)
  }, [size, loaded, user?.uid])
  return [size, set]
}
